import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppHttpService } from './app-http.service';
import { Markup, MarkupHistory } from '../models';
import { AsBuiltReviewStatus, MarkupReviewEvent } from '../models/markup';

/** A PUT carried a stale `expected_version` (409 MARKUP_VERSION_CONFLICT); `current` is the server's row. */
export class MarkupConflictError extends Error {
  constructor(public current: Markup) {
    super('Someone else changed this redline. Reload to see their version.');
  }
}

/** A POST hit an occupied slot (409 MARKUP_EXISTS); `current` is the row holding it. */
export class MarkupExistsError extends Error {
  constructor(public current: Markup) {
    super('A redline already exists here.');
  }
}

/** An as-built mark was reviewed meanwhile (409 MARKUP_AS_BUILT_LOCKED): it is read-only now, so there is no Keep mine. */
export class MarkupLockedError extends Error {
  constructor() {
    super('This as-built mark has been reviewed and can no longer be changed.');
  }
}

export interface AsBuiltReviewBody {
  status: AsBuiltReviewStatus;
  comment?: string | null;
  /** Mark incorporated: a later revision of the same sheet */
  incorporated_revision_uuid?: string | null;
}

export interface RedlineBody {
  annotation_data: any;
  canvas_width: number;
  canvas_height: number;
}

export interface RedlineSlot {
  attachment_id: string;
  page_number: number;
  visibility: 'shared' | 'private';
  /** A record's redline on the sheet (its design-context link); omitted for the page's own rows. */
  link_uuid?: string;
}

@Injectable({ providedIn: 'root' })
export class MarkupService {

  /** Emits after a markup preview has been saved so listeners can refresh thumbnails */
  private markupSavedSource = new Subject<{ attachmentId: string; previewDataUrl?: string }>();
  markupSaved$ = this.markupSavedSource.asObservable();

  /** Persistent cache of markup preview URLs keyed by attachment ID — survives component destruction */
  private markupPreviewCache: Map<string, string> = new Map();

  constructor(
    private appHttpService: AppHttpService
  ) {}

  /**
   * The row a freehand page redline edits: untagged, not a carried copy, not an as-built mark, of the given visibility.
   * The API returns only the caller's private rows, so a private match is always the caller's own.
   */
  static pickPageRedline(rows: Markup[], visibility: 'shared' | 'private'): Markup | null {
    return (rows || []).find(r => r.visibility === visibility && !r.link_uuid && !r.carried_from_uuid && r.kind !== 'as_built') || null;
  }

  notifyMarkupSaved(attachmentId: string, previewDataUrl?: string) {
    if (attachmentId && previewDataUrl) {
      this.markupPreviewCache.set(attachmentId, previewDataUrl);
    }
    this.markupSavedSource.next({ attachmentId, previewDataUrl });
  }

  getMarkupPreview(attachmentId: string): string | undefined {
    return this.markupPreviewCache.get(attachmentId);
  }

  /**
   * Create a new markup
   * @param payload markup data
   */
  create(payload: any) {
    return this.appHttpService.postService('/v1/markup', payload).pipe(
      catchError((e: HttpErrorResponse) => {
        if (e?.status === 409 && e?.error?.code === 'MARKUP_EXISTS' && e?.error?.data) {
          return throwError(() => new MarkupExistsError(new Markup(e.error.data)));
        }
        return throwError(() => e);
      }),
      map((resp: any) => {
        if (resp.data) {
          return new Markup(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Get markups for an attachment
   * @param attachmentId attachment id
   * @param pageNumber optional page number filter
   */
  getByAttachment(attachmentId: string, pageNumber?: number) {
    const qp: any = { attachment_id: attachmentId };
    if (pageNumber !== undefined) {
      qp.page_number = pageNumber;
    }
    return this.appHttpService.getService('/v1/markup', qp).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map(r => new Markup(r));
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Update an existing markup
   * @param id markup id
   * @param payload updated data
   */
  update(id: string, payload: any) {
    return this.appHttpService.putService(`/v1/markup/${id}`, payload).pipe(
      catchError((e: HttpErrorResponse) => {
        if (e?.status === 409 && e?.error?.code === 'MARKUP_VERSION_CONFLICT') {
          return throwError(() => new MarkupConflictError(new Markup(e.error.data)));
        }
        if (e?.status === 409 && e?.error?.code === 'MARKUP_AS_BUILT_LOCKED') {
          return throwError(() => new MarkupLockedError());
        }
        return throwError(() => e);
      }),
      map((resp: any) => {
        if (resp.data) {
          return new Markup(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Save a page redline with optimistic locking. An existing row is a PUT with its version as
   * `expected_version` (visibility is fixed at creation, so it is never sent). An empty slot is a
   * POST with the visibility. Nothing is overwritten without the user's choice: a stale version
   * rejects with MarkupConflictError, and a slot someone filled meanwhile rejects with
   * MarkupExistsError; both carry the server's row so the caller can offer Reload or Keep mine
   * (Keep mine saves again with that row as `existing`).
   * @param existing the row being edited, or null when the slot is empty
   * @param body annotation data and canvas size
   * @param slot where a new row goes
   */
  saveRedline(existing: Markup | null, body: RedlineBody, slot: RedlineSlot): Observable<Markup> {
    if (existing) {
      return this.update(existing.id, { ...body, expected_version: existing.version });
    }
    return this.create({ ...body, ...slot });
  }

  /**
   * Records one as-built mark: always a NEW shared row (spec §15 rule 2). `clientUuid` makes a retry of the same save
   * return the row the first attempt made instead of adding a second (R33), so the caller keeps it until the save lands.
   * With `link_uuid` the mark is a record's (7.2): a real as-built mark, also tagged to the record's link.
   */
  createAsBuilt(body: RedlineBody, slot: Pick<RedlineSlot, 'attachment_id' | 'page_number' | 'link_uuid'>, clientUuid: string): Observable<Markup> {
    return this.create({ ...body, ...slot, visibility: 'shared', kind: 'as_built', client_uuid: clientUuid });
  }

  /** Changes an as-built mark's review status (publish); answers with the updated row. */
  review(id: string, body: AsBuiltReviewBody): Observable<Markup> {
    return this.appHttpService.postService(`/v1/markup/${id}/review`, body).pipe(
      map((resp: { data?: Partial<Markup> } | null) => {
        if (resp?.data) {
          return new Markup(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /** An as-built mark's review trail, oldest first, its carried-from rows' events first (`carried: true`). */
  reviewEvents(id: string): Observable<MarkupReviewEvent[]> {
    return this.appHttpService.getService(`/v1/markup/${id}/review-events`).pipe(
      map((resp: { data?: MarkupReviewEvent[] }) => (Array.isArray(resp?.data) ? resp.data : []))
    );
  }

  /**
   * Delete a markup
   * @param id markup id
   */
  delete(id: string) {
    return this.appHttpService.deleteService(`/v1/markup/${id}`);
  }

  /**
   * Get version history for a markup
   * @param id markup id
   */
  getHistory(id: string) {
    return this.appHttpService.getService(`/v1/markup/${id}/history`).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map(r => new MarkupHistory(r));
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Revert markup to a specific version
   * @param id markup id
   * @param version version number to revert to
   */
  revert(id: string, version: number) {
    return this.appHttpService.postService(`/v1/markup/${id}/revert`, { version }).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new Markup(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Get the number of pages in a PDF attachment
   * @param attachmentId attachment id
   */
  getPdfPageCount(attachmentId: string) {
    return this.appHttpService.getService(`/v1/attachment/${attachmentId}/pdf/page-count`).pipe(
      map((resp: any) => {
        if (resp.data && resp.data.pageCount !== undefined) {
          return resp.data.pageCount as number;
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Get a rendered JPEG image of a specific PDF page
   * @param attachmentId attachment id
   * @param pageNumber 1-based page number
   */
  getPdfPageImage(attachmentId: string, pageNumber: number) {
    return this.appHttpService.getService(
      `/v1/attachment/${attachmentId}/pdf/page/${pageNumber}`,
      {},
      { responseType: 'blob' }
    );
  }

  /**
   * Upload preview image for a markup
   * @param markupId markup id
   * @param imageBlob preview image as Blob
   */
  uploadPreview(markupId: string, imageBlob: Blob) {
    return this.appHttpService.sendMultipartData('POST', `/v1/markup/${markupId}/preview`, {
      previewImage: imageBlob,
    });
  }
}
